import React, { useState, useEffect } from 'react';
import { pocket } from '../api';
import RichTextDisplay from './RichTextDisplay';
import './ThemeTrendsSection.css';

const formatRelativeDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return date.toLocaleDateString();
};

/**
 * One lesson's summary, rendered rich (markdown) and clamped to a few
 * lines by default - tapping it reveals the full summary.
 */
const ThemeTrendLesson = ({ lesson }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`theme-trend-lesson ${expanded ? 'expanded' : ''}`}
      onClick={() => setExpanded(e => !e)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setExpanded(x => !x);
        }
      }}
    >
      <div className="theme-trend-lesson-header">
        <span className="theme-trend-lesson-player">
          👤 {lesson.playerName || 'Unknown'}
          {lesson.isUnassigned && <span className="theme-trend-lesson-badge">unassigned</span>}
        </span>
        <span className="theme-trend-lesson-date">
          {lesson.date ? new Date(lesson.date).toLocaleDateString() : ''}
        </span>
      </div>
      {lesson.summary ? (
        <div className={`theme-trend-lesson-summary ${expanded ? '' : 'clamped'}`}>
          <RichTextDisplay content={lesson.summary} />
        </div>
      ) : (
        <p className="theme-trend-lesson-summary">No summary available</p>
      )}
      <span className="theme-trend-lesson-toggle">{expanded ? 'Show less ▲' : 'Read full summary ▼'}</span>
    </div>
  );
};

/**
 * One theme's bar + count - tapping it lazily loads and expands the list
 * of lessons that mention it, resolved straight from PocketRecording so it
 * works for both unassigned and already-assigned lessons.
 */
const ThemeTrendRow = ({ theme, widthPercent, playerId }) => {
  const [expanded, setExpanded] = useState(false);
  const [lessons, setLessons] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && lessons === null) {
      setLoading(true);
      setError(null);
      try {
        const res = await pocket.getThemeLessons(theme.id, playerId);
        setLessons(res.data?.lessons || []);
      } catch (err) {
        console.error('❌ Error loading theme lessons:', err);
        setError('Failed to load lessons');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className={`theme-trend-row-wrap ${expanded ? 'expanded' : ''}`}>
      <button type="button" className="theme-trend-row" onClick={toggle}>
        <span className="theme-trend-expand-icon">{expanded ? '▼' : '▶'}</span>
        <span className="theme-trend-name">{theme.name}</span>
        <span className="theme-trend-bar-track">
          <span
            className="theme-trend-bar-fill"
            style={{ width: `${widthPercent}%` }}
          />
        </span>
        <span className="theme-trend-meta">
          <span className="theme-trend-count">
            {theme.lessonCount} lesson{theme.lessonCount !== 1 ? 's' : ''}
          </span>
          <span className="theme-trend-date">last {formatRelativeDate(theme.lastDetected)}</span>
        </span>
      </button>

      {expanded && (
        <div className="theme-trend-lessons">
          {loading ? (
            <div className="theme-trends-status">Loading lessons...</div>
          ) : error ? (
            <div className="theme-trends-status theme-trends-status-error">❌ {error}</div>
          ) : lessons.length === 0 ? (
            <div className="theme-trends-status">No lessons found for this theme.</div>
          ) : (
            lessons.map(lesson => (
              <ThemeTrendLesson key={lesson.recordingId} lesson={lesson} />
            ))
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Surfaces ThemeLibrary's per-theme frequency data, which was tracked on
 * every Pocket recording but never shown anywhere before now - turns
 * "themes we happened to detect" into "topics that keep coming up".
 *
 * mode="coach": every theme across the coach's whole roster.
 * mode="player": one player's own recurring topics (coachId optional -
 * backend derives it from the player when omitted).
 */
const ThemeTrendsSection = ({ mode = 'coach', coachId = null, playerId = null, limit = 6 }) => {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    pocket.getThemes(coachId, mode === 'player' ? playerId : null)
      .then(res => {
        if (!cancelled) setThemes(res.data?.themes || []);
      })
      .catch(err => {
        console.error('❌ Error loading theme trends:', err);
        if (!cancelled) setError('Failed to load recurring themes');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [mode, coachId, playerId]);

  const topThemes = themes.slice(0, limit);
  const maxCount = topThemes[0]?.lessonCount || 1;

  return (
    <div className="theme-trends-section">
      <h3 className="theme-trends-title">
        {mode === 'coach' ? '📊 Recurring Focus Areas' : '📊 Your Recurring Focus Areas'}
      </h3>
      <p className="theme-trends-subtitle">
        {mode === 'coach'
          ? 'Topics that keep coming up across your lessons - tap one to see the lessons behind it'
          : 'Topics that keep coming up in your lessons - tap one to see the lessons behind it'}
      </p>

      {loading ? (
        <div className="theme-trends-status">Loading recurring themes...</div>
      ) : error ? (
        <div className="theme-trends-status theme-trends-status-error">❌ {error}</div>
      ) : themes.length === 0 ? (
        <div className="theme-trends-status">
          {mode === 'coach'
            ? 'No recurring themes detected yet. Themes are pulled from your Pocket lesson summaries - once a few recordings come in, patterns will show up here.'
            : 'No recurring focus areas yet. They come from your lesson summaries - once a few lessons have been summarized, patterns will show up here.'}
        </div>
      ) : (
        <div className="theme-trends-list">
          {topThemes.map(theme => (
            <ThemeTrendRow
              key={theme.id}
              theme={theme}
              widthPercent={Math.max(8, (theme.lessonCount / maxCount) * 100)}
              playerId={mode === 'player' ? playerId : null}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ThemeTrendsSection;
